import { useState } from "react";
import type { FormEvent } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";

const FORM_ADDR = "https://readdy.ai/api/form/db90l23k13ra8mig8vng";

const milestones = [
  { year: "2021", title: "起步", desc: "火狼动漫网正式上线，从整理每周新番情报开始，服务第一批动漫爱好者。" },
  { year: "2022", title: "成长", desc: "资讯栏目扩展至行业动态与作品情报，建立稳定的内容更新节奏。" },
  { year: "2023", title: "丰富", desc: "上线动漫作品库，按类型、地区与年份整理作品信息，方便用户检索。" },
  { year: "2024", title: "升级", desc: "重构全站阅读体验，加入专题合集与搜索功能，内容获取更高效。" },
  { year: "2025", title: "持续", desc: "持续深耕动漫内容，围绕最新资讯与热门推荐，为爱好者带来更好的浏览体验。" },
];

const values = [
  { icon: "ri-flashlight-line", title: "及时", desc: "紧跟动漫动态，第一时间整理并发布值得关注的资讯。" },
  { icon: "ri-book-open-line", title: "详实", desc: "每部作品都配有完整的基本信息与剧情简介，方便快速了解。" },
  { icon: "ri-shield-star-line", title: "真诚", desc: "不夸大、不虚构，只提供真实可靠的内容与信息展示。" },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  usePageMeta(
    "关于我们 - 火狼动漫网",
    "了解火狼动漫网的网站定位、发展历程、联系方式与使用说明，我们致力于为动漫爱好者提供最新资讯与热门作品推荐。",
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[{ label: "关于我们" }]} />

        <div className="mt-5 mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">关于火狼动漫网</h1>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            火狼动漫网是一个专注于最新动漫资讯与热门动漫推荐的内容平台，服务广大动漫爱好者，
            帮助大家及时了解动漫动态、发现优质作品，并便捷地获取作品介绍与相关信息。
          </p>
        </div>

        <div className="rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-8 mb-10">
          <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span>
            网站定位
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg bg-background-50 border border-primary-100 p-5">
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-600 text-background-50 mb-4">
                  <i className={`${v.icon} text-xl`}></i>
                </span>
                <h3 className="text-base font-bold text-foreground-900 mb-2">{v.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-10">
          <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span>
            发展历程
          </h2>
          <div className="relative pl-6 border-l-2 border-background-200 space-y-7">
            {milestones.map((m) => (
              <div key={m.year} className="relative">
                <span className="absolute -left-[33px] top-1 w-4 h-4 rounded-full bg-accent-500 border-4 border-background-50"></span>
                <div className="flex items-center gap-3 mb-1.5">
                  <span className="px-2.5 py-0.5 rounded-md bg-primary-100 text-primary-800 text-xs font-bold">
                    {m.year}
                  </span>
                  <h3 className="text-base font-bold text-foreground-900">{m.title}</h3>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="rounded-lg bg-background-100 border border-background-200 p-6">
            <h2 className="text-lg font-bold text-foreground-950 mb-3 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              使用说明
            </h2>
            <ul className="space-y-3 text-sm text-foreground-600 leading-relaxed">
              <li className="flex gap-2">
                <span className="text-primary-600 mt-0.5">·</span>
                本站提供动漫资讯浏览、作品信息查询、分类筛选与关键词搜索等功能。
              </li>
              <li className="flex gap-2">
                <span className="text-primary-600 mt-0.5">·</span>
                收藏功能为本地演示，仅保存在你的浏览器中，刷新后依然保留，不涉及账号登录。
              </li>
              <li className="flex gap-2">
                <span className="text-primary-600 mt-0.5">·</span>
                本站不提供任何视频播放、资源下载或付费服务，所有内容仅用于信息展示与交流。
              </li>
            </ul>
          </div>

          <div className="rounded-lg bg-background-100 border border-background-200 p-6">
            <h2 className="text-lg font-bold text-foreground-950 mb-3 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              版权声明
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed">
              本站所有文字内容与作品信息均为整理与演示用途，图片素材仅用于展示效果。
              如相关内容涉及版权问题，欢迎通过下方反馈入口与我们联系，我们将及时处理。
              本站内容未经许可，请勿用于任何商业用途。
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
          <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-2 flex items-center gap-2">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span>
            联系与反馈
          </h2>
          <p className="text-sm text-foreground-600 mb-6">
            如果你对本站有任何建议、内容纠错或合作意向，欢迎通过下方表单留言，我们会认真阅读每一条反馈。
          </p>

          <form
            id="about-feedback-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl"
          >
            <div className="md:col-span-1">
              <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-800 mb-2">
                你的称呼
              </label>
              <input
                id="fb-name"
                name="name"
                type="text"
                required
                placeholder="请输入称呼"
                className="w-full h-11 px-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
              />
            </div>
            <div className="md:col-span-1">
              <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-800 mb-2">
                邮箱
              </label>
              <input
                id="fb-email"
                name="email"
                type="email"
                required
                placeholder="请输入邮箱"
                className="w-full h-11 px-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-800 mb-2">
                反馈内容
              </label>
              <textarea
                id="fb-message"
                name="message"
                required
                maxLength={500}
                rows={4}
                placeholder="请输入你的建议或想说的话（最多 500 字）"
                className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
              ></textarea>
            </div>

            <input
              className="contact-form-address"
              type="text"
              name="contact_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                type="submit"
                disabled={status === "submitting"}
                className="h-11 px-8 rounded-md bg-primary-600 text-background-50 text-sm font-semibold hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "submitting" ? "提交中..." : "提交反馈"}
              </button>
              {status === "success" && (
                <span className="text-sm text-primary-700 flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line"></i>
                  </span>
                  提交成功，感谢你的反馈！
                </span>
              )}
              {status === "error" && (
                <span className="text-sm text-accent-700 flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-error-warning-line"></i>
                  </span>
                  {formError}
                </span>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}